// SPDX-License-Identifier: Apache-2.0

// ChatHistory — past chat turns, surfaced from the audit log.
//
// Reads `GET /api/audit?tool=brain_chat&limit=200` (the existing endpoint
// already supports the `tool` filter — Hestia's AuditLog viewer uses it).
//
// Each row is one chat turn. The chat-SSE writer records:
//   - `message_preview` — POST-Veil outbound user text (cohort-blended for
//     private-tier, raw for public/internal). For secret-tier deny rows
//     this is the ORIGINAL user message (outbound was never sent).
//   - `reply_preview`   — concatenated `token` events from the agent stream.
//   - Both truncated to ~2 KB; if truncation happened, the preview ends in
//     `… [truncated, full hash <sha256>]` so a forensic reader can recover.
//
// Older rows (written before Spawn 6) lack these fields. We fall back to a
// short metadata-only render rather than blocking; the field is read
// defensively.
//
// MCP unreachable case: same shape as ChatPanel's empty state — inline
// retry + the curl install hint.

import { For, Show, createMemo, createResource, createSignal, onCleanup } from "solid-js";
import { api } from "../lib/api";
import "./sessions/sessions.css";

interface AuditRow {
  ts: string;
  tool: string;
  args_hash: string;
  decision: "allow" | "deny";
  reason: string | null;
  duration_ms: number;
  transport: "stdio" | "ws" | "sse-internal";
  client_id: string;
  approval_id?: string | null;
  // Spawn 6 fields — message_preview / reply_preview (truncated, possibly
  // ending in `… [truncated, full hash <sha256>]`). Older rows skip these.
  message_preview?: string;
  reply_preview?: string;
  // Legacy aliases the previous spawn left behind. Read both for robustness.
  message?: string;
  reply?: string;
  tier?: "public" | "internal" | "private" | "secret";
  did_tools?: number;
}

interface AuditResponse {
  rows: AuditRow[];
  total: number;
  hasMore: boolean;
}

const ENDPOINT = "/api/audit";
const REFRESH_MS = 5000;
const LIMIT = 200;

// Render cap before "show more" toggle kicks in.
const PREVIEW_VISIBLE_LINES = 12;

// Marker the audit writer appends when truncation happened.
const TRUNCATION_MARKER = /…\s*\[truncated,\s*full hash\s+([0-9a-f]{64})\]\s*$/i;

async function fetchHistory(): Promise<AuditResponse> {
  const u = new URL(ENDPOINT, window.location.origin);
  u.searchParams.set("tool", "brain_chat");
  u.searchParams.set("limit", String(LIMIT));
  const r = await api(u.toString(), { headers: { accept: "application/json" } });
  if (!r.ok) throw new Error(`audit ${r.status}`);
  return (await r.json()) as AuditResponse;
}

function fmtAbsTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function fmtRelTs(iso: string, now: number): string {
  try {
    const t = new Date(iso).getTime();
    const age = now - t;
    if (age < 60_000) return `${Math.max(0, Math.floor(age / 1000))}s ago`;
    if (age < 3_600_000) return `${Math.floor(age / 60_000)}m ago`;
    if (age < 86_400_000) return `${Math.floor(age / 3_600_000)}h ago`;
    return `${Math.floor(age / 86_400_000)}d ago`;
  } catch {
    return "—";
  }
}

function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Pull the truncation hint off a preview string. Returns the visible body
 * (without the marker) plus an optional sha256 hash for the badge.
 */
function parsePreview(text: string): { body: string; truncatedHash: string | null } {
  const m = TRUNCATION_MARKER.exec(text);
  if (!m) return { body: text, truncatedHash: null };
  const body = text.slice(0, m.index).replace(/…\s*$/, "").trimEnd();
  return { body, truncatedHash: m[1] ?? null };
}

/** Pick the available preview field, preferring the new one. */
function pickMessage(r: AuditRow): string | undefined {
  return r.message_preview ?? r.message;
}
function pickReply(r: AuditRow): string | undefined {
  return r.reply_preview ?? r.reply;
}

/**
 * Multi-line preview with a "show more" toggle. Counts visual lines after
 * splitting on `\n` — keeps the expanded card from blowing up vertically
 * when the agent dumps a 50-line code block.
 */
function PreviewBlock(props: { label: string; text: string }) {
  const parsed = createMemo(() => parsePreview(props.text));
  const lines = createMemo(() => parsed().body.split(/\r?\n/));
  const [expanded, setExpanded] = createSignal(false);
  const overflowing = createMemo(() => lines().length > PREVIEW_VISIBLE_LINES);
  const visibleLines = createMemo(() =>
    expanded() || !overflowing()
      ? lines()
      : lines().slice(0, PREVIEW_VISIBLE_LINES),
  );
  const hiddenCount = createMemo(() => lines().length - PREVIEW_VISIBLE_LINES);

  return (
    <div class="fleet-history__field fleet-history__field--block">
      <span class="fleet-history__field-label">{props.label}</span>
      <div class="fleet-history__preview">
        <pre class="fleet-history__preview-body">{visibleLines().join("\n")}</pre>
        <Show when={parsed().truncatedHash}>
          {(hash) => (
            <span
              class="fleet-history__truncated-badge"
              title={`forensic hash: ${hash()}`}
            >
              + truncated · sha256 {hash().slice(0, 8)}…
            </span>
          )}
        </Show>
        <Show when={overflowing()}>
          <button
            type="button"
            class="fleet-history__show-more"
            onClick={(e) => {
              // Keep the row click handler from collapsing the whole card.
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded() ? "show less" : `show ${hiddenCount()} more lines`}
          </button>
        </Show>
      </div>
    </div>
  );
}

export default function ChatHistory() {
  const [data, { refetch }] = createResource(fetchHistory);
  const [expandedKey, setExpandedKey] = createSignal<string | null>(null);
  const [now, setNow] = createSignal(Date.now());

  const pollInterval = setInterval(() => void refetch(), REFRESH_MS);
  const tickInterval = setInterval(() => setNow(Date.now()), 30_000);
  onCleanup(() => {
    clearInterval(pollInterval);
    clearInterval(tickInterval);
  });

  const rows = createMemo<AuditRow[]>(() => data()?.rows ?? []);
  const total = createMemo<number>(() => data()?.total ?? 0);
  const fetchErr = createMemo<Error | undefined>(() => data.error as Error | undefined);

  const keyFor = (r: AuditRow, idx: number): string =>
    `${r.client_id}:${r.ts}:${idx}`;

  return (
    <div class="fleet">
      <div class="fleet__bar">
        <span class="fleet__count">
          {total()} {total() === 1 ? "turn" : "turns"} (showing latest {rows().length})
        </span>
        <Show when={data.loading}>
          <span class="fleet__tick">refreshing…</span>
        </Show>
      </div>

      <Show when={fetchErr()}>
        <div class="fleet__error">
          <span class="fleet__error-title">mcp unreachable</span>
          <span>
            couldn't reach {ENDPOINT} — is the local MCP server running?
          </span>
          <pre class="fleet__error-cmd">curl tether.dev/setup | bash</pre>
          <button type="button" class="fleet__retry" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      </Show>

      <Show when={!fetchErr() && rows().length === 0 && !data.loading}>
        <div class="fleet__empty">
          <div class="fleet__empty-eyebrow">no chat turns yet</div>
          <div class="fleet__empty-title">history is empty</div>
          <div class="fleet__empty-copy">
            ask the brain anything in the side rail. every turn lands in the
            audit log and shows up here, newest first.
          </div>
        </div>
      </Show>

      <Show when={rows().length > 0}>
        <div class="fleet-history">
          <For each={rows()}>
            {(row, i) => {
              const key = keyFor(row, i());
              const isOpen = () => expandedKey() === key;
              const msg = () => pickMessage(row);
              const reply = () => pickReply(row);
              return (
                <>
                  <button
                    type="button"
                    class="fleet-history__row"
                    classList={{ "fleet-history__row--expanded": isOpen() }}
                    onClick={() => setExpandedKey(isOpen() ? null : key)}
                    title={fmtAbsTs(row.ts)}
                  >
                    <span class="fleet-history__ts">
                      {fmtRelTs(row.ts, now())}
                    </span>
                    <span class="fleet-history__tier">
                      {row.tier ? row.tier : "—"}
                    </span>
                    <span class={`fleet-history__decision--${row.decision}`}>
                      {row.decision}
                    </span>
                    <span class="fleet-history__duration">
                      {fmtDuration(row.duration_ms)}
                    </span>
                    <span class="fleet-history__chevron">{isOpen() ? "▴" : "▾"}</span>
                  </button>
                  <Show when={isOpen()}>
                    <div class="fleet-history__expanded">
                      <div class="fleet-history__field">
                        <span class="fleet-history__field-label">timestamp</span>
                        <span class="fleet-history__field-value">
                          {fmtAbsTs(row.ts)}
                        </span>
                      </div>
                      <div class="fleet-history__field">
                        <span class="fleet-history__field-label">turn id</span>
                        <span class="fleet-history__field-value">
                          {row.client_id}
                        </span>
                      </div>
                      <div class="fleet-history__field">
                        <span class="fleet-history__field-label">transport</span>
                        <span class="fleet-history__field-value">
                          {row.transport}
                        </span>
                      </div>
                      <div class="fleet-history__field">
                        <span class="fleet-history__field-label">duration</span>
                        <span class="fleet-history__field-value">
                          {fmtDuration(row.duration_ms)}
                        </span>
                      </div>
                      <div class="fleet-history__field">
                        <span class="fleet-history__field-label">decision</span>
                        <span
                          class={`fleet-history__field-value fleet-history__decision--${row.decision}`}
                        >
                          {row.decision}
                          {row.reason ? ` — ${row.reason}` : ""}
                        </span>
                      </div>
                      <Show when={row.tier}>
                        <div class="fleet-history__field">
                          <span class="fleet-history__field-label">tier</span>
                          <span class="fleet-history__field-value">{row.tier}</span>
                        </div>
                      </Show>
                      <Show when={typeof row.did_tools === "number"}>
                        <div class="fleet-history__field">
                          <span class="fleet-history__field-label">tool calls</span>
                          <span class="fleet-history__field-value">
                            {row.did_tools}
                          </span>
                        </div>
                      </Show>
                      <Show when={msg()}>
                        {(m) => <PreviewBlock label="message" text={m()} />}
                      </Show>
                      <Show when={reply()}>
                        {(r) => <PreviewBlock label="reply" text={r()} />}
                      </Show>
                    </div>
                  </Show>
                </>
              );
            }}
          </For>
        </div>
      </Show>
    </div>
  );
}
